import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, IconButton, TextField, Snackbar } from '@mui/material';
import { QrCode, QrCodeScanner, Share, Close, ContentCopy } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import io, { Socket } from 'socket.io-client';
import QRCodeScanner from './QRCodeScanner';
import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { getFirebaseServices } from '../firebase/config';

// 獲取 Firestore 實例
const { db } = getFirebaseServices();

interface SyncPlayManagerProps {
  isOpen: boolean;
  onClose: () => void;
  currentVideoUrl: string;
  currentTime: number;
  isPlaying: boolean;
  numberKeys: Record<string, any>;
  onSyncUpdate?: (data: any) => void;
  onSyncStatusChange?: (isConnected: boolean, isHost: boolean) => void;
  onParticipantCountChange?: (count: number) => void;
  initialSessionId?: string; // 初始會話ID，用於自動加入會話
  user?: any; // 🔧 新增：用戶信息，用於 Firestore 權限驗證
}

interface SyncSession {
  sessionId: string;
  videoUrl: string;
  currentTime: number;
  isPlaying: boolean;
  numberKeys: Record<string, any>;
  hostUserId: string;  // 🔧 修復：改為 hostUserId 以符合 Firestore 規則
  createdAt: Date;
  expiresAt: Date;
}

const SyncPlayManager: React.FC<SyncPlayManagerProps> = ({
  isOpen,
  onClose,
  currentVideoUrl,
  currentTime,
  isPlaying,
  numberKeys,
  onSyncUpdate,
  onSyncStatusChange,
  onParticipantCountChange,
  initialSessionId,
  user  // 🔧 新增：接收用戶信息
}) => {
  const [isHost, setIsHost] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [showQR, setShowQR] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannedData, setScannedData] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [participants, setParticipants] = useState<string[]>([]);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [showShareUrl, setShowShareUrl] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // 確認對話框狀態
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'end' | 'leave' | null>(null);
  
  const socketRef = useRef<any>(null);
  const sessionRef = useRef<SyncSession | null>(null);
  const currentTimeRef = useRef(currentTime); // 🔧 用於避免閉包問題
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null); // 🔧 修復：儲存 setTimeout ID 以便清理

  // 生成隨機會話ID
  const generateSessionId = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  // 將會話信息存儲到Firestore
  const saveSessionToFirestore = async (session: SyncSession) => {
    try {
      // 調試日誌已移除
      // 調試日誌已移除
      
      const { db } = await getFirebaseServices();
      // 調試日誌已移除
      
      const sessionDoc = doc(db, 'syncSessions', session.sessionId);
      // 調試日誌已移除
      
      const sessionData = {
        ...session,
        createdAt: session.createdAt.toISOString(),
        expiresAt: session.expiresAt.toISOString()
      };
      
      // 調試日誌已移除
      // 調試日誌已移除
      
      await setDoc(sessionDoc, sessionData);
      // 調試日誌已移除
    } catch (error) {
      console.error('💾 保存會話信息失敗:', error);
      console.error('💾 錯誤詳情:', {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error; // 重新拋出錯誤，讓調用者知道保存失敗
    }
  };

  // 從Firestore獲取會話信息
  const getSessionFromFirestore = async (sessionId: string): Promise<SyncSession | null> => {
    try {
      console.log('📖 從Firestore獲取會話:', sessionId);
      const { db } = await getFirebaseServices();
      const sessionDoc = doc(db, 'syncSessions', sessionId);
      const sessionSnap = await getDoc(sessionDoc);
      
      console.log('📖 會話文檔存在:', sessionSnap.exists());
      
      if (sessionSnap.exists()) {
        const data = sessionSnap.data();
        console.log('📖 會話數據:', data);
        const session = {
          ...data,
          createdAt: new Date(data.createdAt),
          expiresAt: new Date(data.expiresAt)
        } as SyncSession;
        console.log('📖 解析後的會話:', session);
        return session;
      }
      console.log('📖 會話不存在');
      return null;
    } catch (error) {
      console.error('📖 獲取會話信息失敗:', error);
      return null;
    }
  };

  // 檢查是否有自動加入的會話ID
  useEffect(() => {
    const autoJoinSessionId = localStorage.getItem('autoJoinSessionId');
    if (autoJoinSessionId && isOpen) {
      console.log('自動加入會話:', autoJoinSessionId);
      joinSession(autoJoinSessionId);
      localStorage.removeItem('autoJoinSessionId'); // 清除，避免重複加入
    }
  }, [isOpen]);

  // 清理過期的會話
  const cleanupExpiredSessions = async () => {
    try {
      // 這裡可以添加清理邏輯，但為了簡化，我們在獲取時檢查過期
      console.log('清理過期會話');
    } catch (error) {
      console.error('清理過期會話失敗:', error);
    }
  };

  // 創建主機會話
  const createHostSession = async () => {
    try {
      // 調試日誌已移除
      // 調試日誌已移除
      // 調試日誌已移除
      
      const newSessionId = generateSessionId();
      // 調試日誌已移除
      
      // 🔧 支援訪客使用同步播放
      let hostUserId: string;
      
      if (user?.uid) {
        hostUserId = user.uid;
        console.log('✅ 登入用戶創建同步會話:', user.email);
      } else {
        let guestId = localStorage.getItem('checkplay_guest_id');
        if (!guestId) {
          guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('checkplay_guest_id', guestId);
          console.log('✅ 為訪客生成臨時 ID:', guestId);
        }
        hostUserId = guestId;
        console.log('✅ 訪客創建同步會話:', guestId);
      }
      
      const session: SyncSession = {
        sessionId: newSessionId,
        videoUrl: currentVideoUrl,
        currentTime: currentTime,
        isPlaying: isPlaying,
        numberKeys: numberKeys,
        hostUserId: hostUserId,
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 1000 * 60 * 60) // 1 hour
      };

      // 調試日誌已移除
      
      setSessionId(newSessionId);
      setIsHost(true);
      sessionRef.current = session;
      
      // 保存會話信息到Firestore
      // 調試日誌已移除
      await saveSessionToFirestore(session);
      // 調試日誌已移除
      
      // 生成分享URL
      // 🔧 動態生成分享來源，避免硬編碼 IP
      let shareOrigin = window.location.origin;
      
      // 本機開發環境：使用 localhost
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        shareOrigin = 'http://localhost:5173';
        console.log('🏠 本機開發環境，使用 localhost 生成分享連結');
      }
      // 線上版使用線上域名
      else if (window.location.hostname === 'checkplay.me' || window.location.hostname === 'www.checkplay.me') {
        shareOrigin = 'https://checkplay.me';
        console.log('🌐 線上版，使用線上域名生成分享連結');
      }
      // 區域網路 IP：使用當前 origin（動態）
      else {
        shareOrigin = window.location.origin;
        console.log('🔧 區域網路環境，使用當前 origin:', shareOrigin);
      }
      
      const shareUrl = `${shareOrigin}/sync/${newSessionId}`;
      setShareUrl(shareUrl);
      setShowShareUrl(true);
      // 調試日誌已移除
      
      // 連接到WebSocket服務器（會自動觸發 onSyncStatusChange）
      // 調試日誌已移除
      connectToServer(newSessionId, true);
      
      // 🔧 確保主機狀態正確設置
      // 調試日誌已移除
      // 調試日誌已移除
      // 調試日誌已移除
      
      if (onSyncStatusChange) {
        // 調試日誌已移除
        onSyncStatusChange(true, true);
        // 調試日誌已移除
      } else {
        console.error('❌ onSyncStatusChange 回調函數不存在！');
      }
      
      // 🔧 強制設置本地狀態
      // 調試日誌已移除
      setIsHost(true);
      setIsConnected(true);
      
      // 調試日誌已移除
    } catch (error) {
      console.error('🎯 創建主機會話失敗:', error);
      setSnackbarMessage('創建會話失敗');
      setSnackbarOpen(true);
    }
  };

  // 加入會話
  const joinSession = async (sessionData: string) => {
    try {
      console.log('🔍 開始加入會話:', sessionData);
      
      // 從Firestore獲取會話信息
      console.log('🔍 從 Firestore 獲取會話信息...');
      const session = await getSessionFromFirestore(sessionData);
      console.log('🔍 Firestore 查詢結果:', session ? '找到會話' : '會話不存在');
      
      if (!session) {
        console.error('🔍 會話不存在或已過期');
        setSnackbarMessage('會話不存在或已過期，請確認A用戶已創建會話');
        setSnackbarOpen(true);
        return;
      }

      // 檢查會話是否過期
      console.log('🔍 檢查會話過期時間:', session.expiresAt);
      if (new Date() > session.expiresAt) {
        console.error('🔍 會話已過期');
        setSnackbarMessage('會話已過期，請重新創建會話');
        setSnackbarOpen(true);
        return;
      }
      
      // 調試日誌已移除
      setSessionId(session.sessionId);
      setIsHost(false);
      sessionRef.current = session;
      
      // 🔧 先通知父組件同步狀態變化（設置 isOnAir = true）
      // 這樣後續的 onSyncUpdate 才能正確處理
      if (onSyncStatusChange) {
        // 調試日誌已移除
        onSyncStatusChange(true, false); // 連接成功，不是主機
      }
      
      // 連接到WebSocket服務器
      // 調試日誌已移除
      connectToServer(session.sessionId, false);
      
      // 🔧 延遲調用 onSyncUpdate，確保 isOnAir 已經設置
      setTimeout(() => {
        if (onSyncUpdate) {
          // 調試日誌已移除
          onSyncUpdate(session);
        } else {
          // 調試日誌已移除
        }
      }, 100);
      
      // 調試日誌已移除
    } catch (error) {
      console.error('加入會話失敗:', error);
      console.error('錯誤詳情:', {
        name: error.name,
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      setSnackbarMessage('加入會話失敗: ' + error.message);
      setSnackbarOpen(true);
    }
  };

  // 連接到WebSocket服務器
  const connectToServer = (sessionId: string, isHost: boolean) => {
    // 根據當前域名動態選擇WebSocket服務器地址
    let serverUrl = import.meta.env.VITE_WS_SERVER || 'ws://localhost:3003';
    
    // 本機開發環境：使用 localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      serverUrl = 'ws://localhost:3003';
      console.log('🏠 本機開發環境，使用 localhost WebSocket');
    }
    // 線上版使用 Cloud Run 的 WebSocket 服務器
    else if (window.location.hostname === 'checkplay.me' || window.location.hostname === 'www.checkplay.me') {
      serverUrl = 'wss://checkplay-sync-230168256043.asia-east1.run.app';
      console.log('🌐 線上版，使用 Cloud Run WebSocket 服務器');
    }
    // ngrok 環境
    else if (window.location.hostname.includes('ngrok-free.app')) {
      serverUrl = 'ws://localhost:3003';
      console.log('🔧 ngrok 環境，使用 localhost WebSocket');
    }
    // 區域網路：動態使用當前 hostname
    else {
      const currentHost = window.location.hostname;
      serverUrl = `ws://${currentHost}:3003`;
      console.log('🔧 區域網路環境，使用當前 hostname:', serverUrl);
    }
    
    console.log('🔌 連接到WebSocket服務器:', serverUrl);
    
    socketRef.current = io(serverUrl, {
      query: { sessionId, isHost }
    });

    socketRef.current.on('connect', () => {
      setIsConnected(true);
      console.log('✅ WebSocket已連接');
      // 🔧 不重複調用 onSyncStatusChange，因為在加入會話時已經調用過了
    });

    socketRef.current.on('disconnect', (reason) => {
      setIsConnected(false);
      console.log('❌ 與同步服務器斷開連接，原因:', reason);
      // 通知父組件同步狀態變化
      if (onSyncStatusChange) {
        onSyncStatusChange(false, isHost);
      }
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('❌ WebSocket連接錯誤:', error);
      setIsConnected(false);
      
      // 檢查是否為 CSP 錯誤
      const errorMsg = error?.toString() || '';
      if (errorMsg.includes('xhr poll error') || errorMsg.includes('transport error')) {
        console.error('🚨 可能是 CSP 問題或網路問題，請檢查瀏覽器控制台');
        console.error('💡 解決方法：');
        console.error('   1. 使用無痕視窗訪問 https://checkplay.me');
        console.error('   2. 或按 Cmd+Shift+R (Mac) / Ctrl+Shift+R (Windows) 強制刷新');
        console.error('   3. 或訪問 https://checkplay-fs.web.app 繞過 CDN');
        
        setSnackbarMessage('連線失敗！請使用無痕視窗或清除瀏覽器快取後重試');
      } else {
        setSnackbarMessage('無法連接到同步服務器');
      }
      
      setSnackbarOpen(true);
    });

    socketRef.current.on('participantJoined', (participantId: string) => {
      setParticipants(prev => {
        const newParticipants = [...prev, participantId];
        // 通知父組件參與者數量變化
        onParticipantCountChange?.(newParticipants.length);
        return newParticipants;
      });
      
      // 主機模式下，當有新參與者加入時，立即發送當前狀態
      if (isHost) {
        const currentState = {
          videoUrl: currentVideoUrl,
          currentTime: currentTime,
          isPlaying: isPlaying,
          numberKeys: numberKeys
        };
        sendSyncUpdate(currentState);
      }
    });

    socketRef.current.on('participantLeft', (participantId: string) => {
      setParticipants(prev => {
        const newParticipants = prev.filter(id => id !== participantId);
        // 通知父組件參與者數量變化
        onParticipantCountChange?.(newParticipants.length);
        return newParticipants;
      });
    });

    // 🔧 新增：監聽主機斷開事件
    socketRef.current.on('hostDisconnected', (reason: string) => {
      console.log('❌ 主機已斷開連接，原因:', reason);
      setIsConnected(false);
      
      // 通知父組件同步狀態變化
      if (onSyncStatusChange) {
        onSyncStatusChange(false, false);
      }
      
      // 顯示提示訊息
      setSnackbarMessage('主機已結束會話，連線已中斷');
      setSnackbarOpen(true);
      
      // 🔧 修復：自動關閉同步播放管理視窗，並儲存 timeout ID 以便清理
      closeTimeoutRef.current = setTimeout(() => {
        onClose();
      }, 2000); // 2秒後自動關閉
    });

    socketRef.current.on('syncUpdate', (data: any) => {
      console.log('📡 收到 syncUpdate 事件:', data);
      console.log('📡 當前狀態:', { isHost, hasOnSyncUpdate: !!onSyncUpdate });
      
      if (!isHost && onSyncUpdate) {
        console.log('📡 參與者模式，調用 onSyncUpdate');
        onSyncUpdate(data);
      } else if (isHost) {
        console.log('📡 主機模式，忽略 syncUpdate（主機不接收同步）');
      } else if (!onSyncUpdate) {
        console.error('📡 onSyncUpdate 回調不存在！');
      }
    });
  };

  // 發送同步更新
  const sendSyncUpdate = (updateData: any) => {
    // 調試日誌已移除
    
    if (socketRef.current && isHost && sessionId) {
      // 調試日誌已移除
      socketRef.current.emit('syncUpdate', {
        sessionId,
        ...updateData
      });
    } else {
      console.warn('⚠️ 無法發送同步更新:', { 
        hasSocket: !!socketRef.current, 
        isHost, 
        hasSessionId: !!sessionId 
      });
    }
  };

  // 顯示確認對話框
  const showConfirmDialog = (action: 'end' | 'leave') => {
    setConfirmAction(action);
    setConfirmDialogOpen(true);
  };

  // 確認結束會話
  const confirmEndSession = async () => {
    try {
      console.log('🛑 確認結束會話:', sessionId);
      
      // 斷開WebSocket連接
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      
      // 如果是主機，刪除Firestore中的會話數據
      if (isHost && sessionId) {
        try {
          const sessionDocRef = doc(db, 'syncSessions', sessionId);
          await deleteDoc(sessionDocRef);
          // 調試日誌已移除
        } catch (firestoreError) {
          console.warn('⚠️ 刪除Firestore會話失敗:', firestoreError);
          // 即使刪除失敗也繼續執行
        }
      }
      
      // 重置所有狀態
      setSessionId('');
      setIsHost(false);
      setIsConnected(false);
      setParticipants([]);
      setScannedData('');
      sessionRef.current = null;
      
      // 通知父組件同步狀態變化
      onSyncStatusChange(false, false);
      onParticipantCountChange?.(0);
      
      // 關閉確認對話框
      setConfirmDialogOpen(false);
      setConfirmAction(null);
      
      // 🔧 關閉主對話框
      onClose();
      
      // 顯示成功訊息
      setSnackbarMessage('連線中斷');
      setSnackbarOpen(true);
      
      // 調試日誌已移除
    } catch (error) {
      console.error('❌ 結束會話失敗:', error);
      setSnackbarMessage('結束會話失敗');
      setSnackbarOpen(true);
    }
  };

  // 取消確認
  const cancelConfirm = () => {
    setConfirmDialogOpen(false);
    setConfirmAction(null);
  };

  // 生成QR Code數據 - 使用useMemo緩存，避免重複調用
  const qrCodeData = useMemo(() => {
    if (sessionRef.current) {
      // 只包含會話ID，其他信息從Firestore獲取
      const sessionId = sessionRef.current.sessionId;
      // 調試日誌已移除
      return sessionId;
    }
    // 調試日誌已移除
    return '';
  }, [sessionRef.current?.sessionId]); // 只有sessionId變化時才重新計算

  // 掃描QR Code
  const handleScan = (data: string) => {
    // 調試日誌已移除
    // 調試日誌已移除
    // 調試日誌已移除
    
    if (data) {
      // 清理數據，移除可能的URL前綴
      let sessionId = data.trim();
      // 調試日誌已移除
      
      // 如果是完整URL，提取會話ID
      if (sessionId.includes('/sync/')) {
        const urlParts = sessionId.split('/sync/');
        sessionId = urlParts[urlParts.length - 1];
        // 調試日誌已移除
      }
      
      // 調試日誌已移除
      // 調試日誌已移除
      
      setScannedData(sessionId);
      setShowScanner(false);
      
      // 添加錯誤處理
      try {
        joinSession(sessionId);
        // 調試日誌已移除
      } catch (error) {
        console.error('🔍 joinSession 調用失敗:', error);
        setSnackbarMessage('加入會話失敗: ' + error.message);
        setSnackbarOpen(true);
      }
    } else {
      console.error('🔍 掃描數據為空');
      setSnackbarMessage('掃描失敗：未檢測到QR Code');
      setSnackbarOpen(true);
    }
  };

  // 複製分享URL
  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSnackbarMessage('URL已複製到剪貼板');
      setSnackbarOpen(true);
      
      // 🔧 複製成功後不自動關閉視窗，讓用戶選擇「確認連線」或「離開會話」
      // 調試日誌已移除
    } catch (error) {
      console.error('複製失敗:', error);
      setSnackbarMessage('複製失敗');
      setSnackbarOpen(true);
    }
  };

  // 處理掃描錯誤
  const handleScanError = (error: any) => {
    console.error('QR Code掃描錯誤:', error);
    setSnackbarMessage('掃描失敗');
    setSnackbarOpen(true);
  };

  // 處理初始會話ID - 自動加入會話
  useEffect(() => {
    console.log('🔍 檢查初始會話 ID:', { initialSessionId, isOpen, sessionId });
    
    if (initialSessionId && isOpen && !sessionId) {
      console.log('✅ 檢測到初始會話 ID，自動加入會話:', initialSessionId);
      setScannedData(initialSessionId);
      joinSession(initialSessionId);
    } else {
      console.log('⏭️ 跳過自動加入會話');
    }
  }, [initialSessionId, isOpen, sessionId]);

  // 清理連接
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      // 🔧 修復：清理所有 setTimeout，防止記憶體洩漏
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        // 調試日誌已移除
      }
    };
  }, []);

  // 🔧 使用 ref 來追蹤上一次的播放狀態，避免重複發送
  const lastSyncDataRef = useRef<{
    isPlaying: boolean;
    currentTime: number;
    numberKeysHash: string;
  } | null>(null);

  // 主機模式下，當播放狀態改變時自動發送同步更新
  useEffect(() => {
    // 調試日誌已移除
    
    if (isHost && isConnected && sessionRef.current) {
      const currentNumberKeysHash = JSON.stringify(numberKeys);
      const currentSyncData = {
        isPlaying,
        currentTime: currentTimeRef.current,
        numberKeysHash: currentNumberKeysHash
      };
      
      // 檢查是否需要發送更新（避免重複發送相同狀態）
      const shouldUpdate = !lastSyncDataRef.current ||
        lastSyncDataRef.current.isPlaying !== currentSyncData.isPlaying ||
        lastSyncDataRef.current.numberKeysHash !== currentSyncData.numberKeysHash ||
        Math.abs(lastSyncDataRef.current.currentTime - currentSyncData.currentTime) > 1; // 時間差異大於1秒才更新
      
      if (shouldUpdate) {
        // 🔧 當 numberKeys 變化時（用戶點擊藍色重點），使用最新的 currentTime prop
        // 因為 MainPage 在跳轉時會立即更新 playedSeconds 狀態
        const syncTime = lastSyncDataRef.current?.numberKeysHash !== currentNumberKeysHash 
          ? currentTime // numberKeys 變化時使用最新的 currentTime（跳轉後的時間）
          : currentTimeRef.current; // 其他情況使用 ref
        
        const updateData = {
          videoUrl: currentVideoUrl,
          currentTime: syncTime,
          isPlaying: isPlaying,
          numberKeys: numberKeys
        };
        // 調試日誌已移除
        // 調試日誌已移除
        sendSyncUpdate(updateData);
        
        // 更新上一次的同步數據
        lastSyncDataRef.current = currentSyncData;
      } else {
        console.log('⏭️ 狀態沒有實質變化，跳過同步更新');
      }
    }
  }, [isHost, isConnected, currentVideoUrl, isPlaying, numberKeys]);
  
  // 🔧 更新 currentTime ref
  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  // 🔧 移除自動時間同步：只在用戶主動操作時才同步（暫停/播放/跳轉/藍色重點）
  // 這樣可以避免用戶B在播放中被不斷打斷執行 seekTo()
  // 時間同步現在只通過以下方式觸發：
  // 1. 用戶A暫停/播放（由 isPlaying 變化觸發）
  // 2. 用戶A點擊藍色重點（由 numberKeys 變化觸發）
  // 3. 用戶A手動拖動進度條（需要在 MainPage 中添加手動觸發）

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">CheckPlay 同步播放管理</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {!sessionId ? (
          // 初始選擇界面
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body1" gutterBottom>
              選擇同步播放模式：
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<QrCode />}
              onClick={() => {
                // 調試日誌已移除
                // 調試日誌已移除
                createHostSession();
              }}
              fullWidth
              sx={{ 
                backgroundColor: '#d32f2f', 
                '&:hover': { backgroundColor: '#b71c1c' },
                mb: 2 
              }}
            >
              創建CheckPlay同步會話（A地點）
            </Button>
            
            {/* 測試按鈕 */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                console.log('🧪 測試：創建測試會話');
                const testSessionId = 'test-session-' + Date.now();
                console.log('🧪 測試會話ID:', testSessionId);
                setScannedData(testSessionId);
                joinSession(testSessionId);
              }}
              fullWidth
            >
              測試：模擬加入會話
            </Button>
            
            {/* 檢查會話按鈕 */}
            <Button
              variant="outlined"
              size="small"
              onClick={async () => {
                if (sessionRef.current) {
                  // 調試日誌已移除
                  const session = await getSessionFromFirestore(sessionRef.current.sessionId);
                  // 調試日誌已移除
                  if (session) {
                    setSnackbarMessage('會話存在於Firestore');
                  } else {
                    setSnackbarMessage('會話不存在於Firestore');
                  }
                  setSnackbarOpen(true);
                } else {
                  setSnackbarMessage('沒有當前會話');
                  setSnackbarOpen(true);
                }
              }}
              fullWidth
            >
              檢查：當前會話是否存在
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<QrCodeScanner />}
              onClick={() => setShowScanner(true)}
              fullWidth
            >
              掃描QR Code加入CheckPlay會話（B地點）
            </Button>
          </Box>
        ) : (
          // 會話管理界面
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="h6">
              {isHost ? '主機模式' : '參與者模式'}
            </Typography>
            
            <Typography variant="body2" color="textSecondary">
              連線ID: {sessionId}
            </Typography>
            
            <Typography variant="body2" color="textSecondary">
              連接狀態: {isConnected ? '連線成功' : '未連接'}
            </Typography>
            
            {isHost && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  目前連線數量: {participants.length}
                </Typography>
                
                <Button
                  variant="outlined"
                  startIcon={<QrCode />}
                  onClick={() => setShowQR(true)}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  顯示QR Code
                </Button>

                {showShareUrl && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      分享連結：
                    </Typography>
                    <Box display="flex" gap={1}>
                      <TextField
                        value={shareUrl}
                        size="small"
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                      <IconButton onClick={copyShareUrl} size="small">
                        <ContentCopy />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {/* 結束會話和複製連結按鈕 */}
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={copyShareUrl}
                    sx={{ flex: 1 }}
                  >
                    複製連結
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => showConfirmDialog('end')}
                    sx={{ flex: 1 }}
                  >
                    結束會話
                  </Button>
                </Box>
              </Box>
            )}

            {/* 參與者按鈕區域 */}
            {!isHost && (
              <Box display="flex" gap={1}>
                {/* 分享連線按鈕 */}
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={copyShareUrl}
                  sx={{ flex: 1 }}
                >
                  分享連線
                </Button>
                
                {/* 確認連線按鈕 */}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onClose}
                  sx={{ flex: 1 }}
                >
                  確認連線
                </Button>
                
                {/* 離開會話按鈕 */}
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => showConfirmDialog('leave')}
                  sx={{ flex: 1 }}
                >
                  離開會話
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* QR Code顯示對話框 */}
      <Dialog open={showQR} onClose={() => setShowQR(false)}>
        <DialogTitle>掃描此QR Code加入CheckPlay同步播放</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" p={2}>
            <QRCodeSVG value={qrCodeData} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQR(false)}>關閉</Button>
        </DialogActions>
      </Dialog>

      {/* QR Code掃描對話框 */}
      <Dialog open={showScanner} onClose={() => setShowScanner(false)}>
        <DialogTitle>掃描QR Code</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" p={2}>
            <QRCodeScanner 
              onScan={handleScan} 
              onError={handleScanError} 
              onClose={() => setShowScanner(false)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowScanner(false)}>取消</Button>
        </DialogActions>
      </Dialog>

      {/* 確認對話框 */}
      <Dialog open={confirmDialogOpen} onClose={cancelConfirm}>
        <DialogTitle>確認中斷同步播放功能</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmAction === 'end' 
              ? '確定要結束同步播放會話嗎？所有參與者將被斷開連接。'
              : '確定要離開同步播放會話嗎？'
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelConfirm} color="primary">
            取消
          </Button>
          <Button 
            onClick={confirmEndSession} 
            color="error" 
            variant="contained"
          >
            確定
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar通知 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Typography>{snackbarMessage}</Typography>
      </Snackbar>
    </Dialog>
  );
};

export default SyncPlayManager; 